"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.localizePropertyValue = exports.propertyTypeToHeader = exports.localizeActionType = void 0;
/* eslint-disable @typescript-eslint/no-var-requires */
const dotty = require('dotty');
const util = require("util");
const utils_1 = require("./utils");
/**
 * Converts the name of a workflow action into a human-readable string, using the workflow's schema
 * For example, this might convert a workflow action called "PainLevel" to "Pain Level"
 * @param {WorkflowSchema} workflowSchema - The mongodb object containing the schema for the workflow
 * @param {string} action - The type of action to localize (stored in workflow actions under the key "action")
 * @param {string} [language="en"] - The language to localize to
 *
 * @returns {string} The localization. Defaults to action if not found.
 */
function localizeActionType(workflowSchema, action, language = 'en') {
    var _a, _b, _c;
    action = action.toString();
    const cleanedActionName = (0, utils_1.removeSpecialCharacters)(action);
    const splitActionName = (0, utils_1.splitCamelCase)(cleanedActionName);
    if (!workflowSchema) {
        if (splitActionName.length > 31) {
            return `${splitActionName.substring(0, 28)}...`;
        }
        return splitActionName;
    }
    const localization = ((_c = (_b = (_a = workflowSchema === null || workflowSchema === void 0 ? void 0 : workflowSchema.schema) === null || _a === void 0 ? void 0 : _a.actions) === null || _b === void 0 ? void 0 : _b[action]) === null || _c === void 0 ? void 0 : _c.localization) || {};
    const localized = localization[language] || localization['en'] || splitActionName;
    if (localized.length > 31)
        return `${localized.substring(0, 28)}...`;
    return localized;
}
exports.localizeActionType = localizeActionType;
/**
 * Converts the key for a property of a workflow action into a human-readable string, using the workflow's schema
 * In general, these are likely going to be the question fields of a form.
 * For example, this might convert "Question2" to "What is your marital status?"
 * @param {Record<string, unknown>} aeSchema - The mongodb object containing the schema for the workflow
 * @param {string} actionType - The type of action that this property is a part of
 * @param {string} propertyType - The property to localize
 * @param {string} [language="en"] - The language to localize to
 *
 * @returns {string} The localization. Defaults to propertyType if not found.
 */
function propertyTypeToHeader(aeSchema, propertyAccessor, raw, language) {
    var _a;
    language = language || 'en';
    const propertySchema = dotty.get(aeSchema, propertyAccessor);
    // This turns something like ["properties", "questions", "properties", "page1", "properties", "question1"] into "question1_page1"
    const label = propertyAccessor
        .filter((property) => property !== 'properties')
        .slice()
        .reverse()
        .join('_')
        .replace(/[^0-9a-zA-Z_ ]/g, '');
    if (raw)
        return `${label} (raw)`;
    let localized = ((_a = propertySchema === null || propertySchema === void 0 ? void 0 : propertySchema.localization) === null || _a === void 0 ? void 0 : _a[language]) || '';
    localized = localized.toString().replace(/[^0-9a-zA-Z_ ]/g, '');
    return localized ? `${label} (${localized})` : label;
}
exports.propertyTypeToHeader = propertyTypeToHeader;
/**
 * Converts the value for a property of a workflow action into a human-readable string, using the workflow's schema
 * In general, these are likely going to be answers to question of a form.
 * For example, this might convert "answer2" to "Unmarried"
 * @param {Mongodb_Document} workflowSchema - The mongodb object containing the schema for the workflow
 * @param {string} actionType - The type of action that this property is a part of
 * @param {string} propertyType - The property this is a value for
 * @param {string} value - The value to localize
 * @param {string} [language="en"] - The language to localize to
 *
 * @returns {string} The localization. Defaults to value if not found.
 */
function localizePropertyValue(workflowSchema, actionType, dataAccessor, value, language = 'en') {
    var _a, _b, _c, _d, _e, _f;
    if (typeof value != 'string' || typeof value != 'number')
        throw new Error(`Received illegal value of ${value} in localizePropertyValue`);
    language = language || 'en';
    const propertySchema = dotty.get((_c = (_b = (_a = workflowSchema === null || workflowSchema === void 0 ? void 0 : workflowSchema.schema) === null || _a === void 0 ? void 0 : _a.actions) === null || _b === void 0 ? void 0 : _b[actionType]) === null || _c === void 0 ? void 0 : _c.properties, dataAccessor);
    let localized = ((_e = (_d = propertySchema === null || propertySchema === void 0 ? void 0 : propertySchema.value_localization) === null || _d === void 0 ? void 0 : _d[language]) === null || _e === void 0 ? void 0 : _e[value]) ||
        ((_f = propertySchema === null || propertySchema === void 0 ? void 0 : propertySchema.value_localization) === null || _f === void 0 ? void 0 : _f[value]);
    if (!propertySchema || typeof localized === 'undefined' || localized === null)
        return null;
    if (!(typeof localized === 'string' || typeof localized === 'number'))
        return util.inspect(localized);
    localized = localized.toString().replace(/[^0-9a-zA-Z_ ]/g, '');
    return localized;
}
exports.localizePropertyValue = localizePropertyValue;
//# sourceMappingURL=localization-utils.js.map